import type { Declaration, Expression, GlossaProgram, LValue, Statement, SwitchCaseValue } from '../parser/ast.js';

/**
 * Η ΓΛΩΣΣΑ (σε αντίθεση με την Ψευδογλώσσα) απαιτεί ΚΑΘΕ μεταβλητή/πίνακα
 * να έχει δηλωθεί ρητά στο ΜΕΤΑΒΛΗΤΕΣ πριν χρησιμοποιηθεί — ΚΑΙ ο μετρητής
 * ενός ΓΙΑ. Αυτός ο έλεγχος τρέχει ΠΡΙΝ την εκτέλεση (σαν συντακτικό
 * σφάλμα, όχι σφάλμα εκτέλεσης) ώστε να πιάνει αδήλωτες μεταβλητές ΑΚΟΜΑ ΚΙ
 * αν βρίσκονται σε κλάδο/βρόχο που δεν θα εκτελούνταν ποτέ σε αυτό το
 * συγκεκριμένο τρέξιμο (π.χ. μέσα σε ΑΝ που δεν ισχύει).
 *
 * Μόνο για ΠΛΗΡΕΣ πρόγραμμα ΓΛΩΣΣΑΣ (`GlossaProgram`) — το "τμήμα
 * αλγορίθμου" δεν έχει καν τμήμα ΜΕΤΑΒΛΗΤΕΣ, άρα δεν εφαρμόζεται εδώ.
 */
export interface UndeclaredReference {
  readonly name: string;
  readonly line: number;
  readonly column: number;
}

function collectDeclaredNames(declarations: readonly Declaration[]): Set<string> {
  const names = new Set<string>();
  for (const decl of declarations) {
    if (decl.kind === 'ConstantDeclaration') names.add(decl.name);
    else for (const d of decl.declarators) names.add(d.name);
  }
  return names;
}

function checkDeclarationExpressions(declarations: readonly Declaration[], known: ReadonlySet<string>): UndeclaredReference | null {
  for (const decl of declarations) {
    if (decl.kind === 'ConstantDeclaration') {
      const result = checkExpr(decl.value, known);
      if (result !== null) return result;
      continue;
    }
    for (const declarator of decl.declarators) {
      for (const dim of declarator.dimensions) {
        const result = checkExpr(dim, known);
        if (result !== null) return result;
      }
    }
  }
  return null;
}

function checkExpr(expr: Expression, known: ReadonlySet<string>): UndeclaredReference | null {
  switch (expr.kind) {
    case 'NumberLiteral':
    case 'StringLiteral':
    case 'BooleanLiteral':
      return null;
    case 'Identifier':
      return known.has(expr.name) ? null : { name: expr.name, line: expr.line, column: expr.column };
    case 'ArrayAccess': {
      if (!known.has(expr.array.name)) return { name: expr.array.name, line: expr.array.line, column: expr.array.column };
      for (const idx of expr.indices) {
        const result = checkExpr(idx, known);
        if (result !== null) return result;
      }
      return null;
    }
    // Το callee είναι όνομα έτοιμης μαθηματικής ή ΣΥΝΑΡΤΗΣΗΣ του μαθητή —
    // ΔΕΝ είναι μεταβλητή, δεν ελέγχεται εδώ (η ύπαρξή του ελέγχεται ήδη
    // ρητά στον Interpreter, με δικό του σαφές μήνυμα).
    case 'FunctionCall': {
      for (const arg of expr.args) {
        const result = checkExpr(arg, known);
        if (result !== null) return result;
      }
      return null;
    }
    case 'UnaryExpression':
      return checkExpr(expr.operand, known);
    case 'BinaryExpression': {
      const left = checkExpr(expr.left, known);
      if (left !== null) return left;
      return checkExpr(expr.right, known);
    }
  }
}

function checkLValue(lvalue: LValue, known: ReadonlySet<string>): UndeclaredReference | null {
  if (lvalue.kind === 'Identifier') {
    return known.has(lvalue.name) ? null : { name: lvalue.name, line: lvalue.line, column: lvalue.column };
  }
  if (!known.has(lvalue.array.name)) return { name: lvalue.array.name, line: lvalue.array.line, column: lvalue.array.column };
  for (const idx of lvalue.indices) {
    const result = checkExpr(idx, known);
    if (result !== null) return result;
  }
  return null;
}

function checkSwitchCaseValue(value: SwitchCaseValue, known: ReadonlySet<string>): UndeclaredReference | null {
  if (value.kind === 'CaseRange') {
    const from = checkExpr(value.from, known);
    if (from !== null) return from;
    return checkExpr(value.to, known);
  }
  if (value.kind === 'CaseComparison') return checkExpr(value.value, known);
  return checkExpr(value, known);
}

function checkStatement(stmt: Statement, known: ReadonlySet<string>): UndeclaredReference | null {
  switch (stmt.kind) {
    case 'AssignmentStatement': {
      const value = checkExpr(stmt.value, known);
      if (value !== null) return value;
      return checkLValue(stmt.target, known);
    }
    case 'ReadStatement': {
      for (const target of stmt.targets) {
        const result = checkLValue(target, known);
        if (result !== null) return result;
      }
      return null;
    }
    case 'WriteStatement': {
      for (const value of stmt.values) {
        const result = checkExpr(value, known);
        if (result !== null) return result;
      }
      return null;
    }
    case 'SwapStatement': {
      const left = checkLValue(stmt.left, known);
      if (left !== null) return left;
      return checkLValue(stmt.right, known);
    }
    case 'IfStatement': {
      const condition = checkExpr(stmt.condition, known);
      if (condition !== null) return condition;
      const thenResult = checkStatements(stmt.thenBranch, known);
      if (thenResult !== null) return thenResult;
      for (const branch of stmt.elseIfBranches) {
        const branchCondition = checkExpr(branch.condition, known);
        if (branchCondition !== null) return branchCondition;
        const branchResult = checkStatements(branch.body, known);
        if (branchResult !== null) return branchResult;
      }
      if (stmt.elseBranch !== null) return checkStatements(stmt.elseBranch, known);
      return null;
    }
    case 'ForStatement': {
      if (!known.has(stmt.variable.name)) {
        return { name: stmt.variable.name, line: stmt.variable.line, column: stmt.variable.column };
      }
      const from = checkExpr(stmt.from, known);
      if (from !== null) return from;
      const to = checkExpr(stmt.to, known);
      if (to !== null) return to;
      if (stmt.step !== null) {
        const step = checkExpr(stmt.step, known);
        if (step !== null) return step;
      }
      return checkStatements(stmt.body, known);
    }
    case 'WhileStatement': {
      const condition = checkExpr(stmt.condition, known);
      if (condition !== null) return condition;
      return checkStatements(stmt.body, known);
    }
    case 'RepeatUntilStatement': {
      const bodyResult = checkStatements(stmt.body, known);
      if (bodyResult !== null) return bodyResult;
      return checkExpr(stmt.condition, known);
    }
    case 'SwitchStatement': {
      const discriminant = checkExpr(stmt.discriminant, known);
      if (discriminant !== null) return discriminant;
      for (const switchCase of stmt.cases) {
        for (const value of switchCase.values) {
          const valueResult = checkSwitchCaseValue(value, known);
          if (valueResult !== null) return valueResult;
        }
        const bodyResult = checkStatements(switchCase.body, known);
        if (bodyResult !== null) return bodyResult;
      }
      if (stmt.elseBody !== null) return checkStatements(stmt.elseBody, known);
      return null;
    }
    case 'CallStatement': {
      for (const arg of stmt.args) {
        const result = checkExpr(arg, known);
        if (result !== null) return result;
      }
      return null;
    }
  }
}

function checkStatements(statements: readonly Statement[], known: ReadonlySet<string>): UndeclaredReference | null {
  for (const stmt of statements) {
    const result = checkStatement(stmt, known);
    if (result !== null) return result;
  }
  return null;
}

/**
 * Επιστρέφει την ΠΡΩΤΗ αδήλωτη μεταβλητή/πίνακα (με σειρά εμφάνισης στον
 * κώδικα — κύριο πρόγραμμα πρώτα, μετά κάθε υποπρόγραμμα με τη σειρά τους),
 * ή `null` αν όλα είναι δηλωμένα. Κάθε υποπρόγραμμα ελέγχεται με τη ΔΙΚΗ
 * ΤΟΥ εμβέλεια (δικές του δηλώσεις + —για ΣΥΝΑΡΤΗΣΗ— το ίδιο του το
 * όνομα) — ΟΧΙ τις δηλώσεις του κυρίου προγράμματος, αφού τα υποπρογράμματα
 * έχουν αυστηρά τοπική εμβέλεια (βλ. Interpreter.callSubprogram). ΚΑΘΕ
 * παράμετρος πρέπει ΕΠΙΣΗΣ να έχει ρητή δήλωση στο ΜΕΤΑΒΛΗΤΕΣ (σύμβαση
 * βιβλίου) — δεν εξαιρείται απλά επειδή εμφανίζεται στη λίστα παραμέτρων.
 */
export function findUndeclaredGlossaReference(program: GlossaProgram): UndeclaredReference | null {
  const mainNames = collectDeclaredNames(program.declarations);
  const declResult = checkDeclarationExpressions(program.declarations, mainNames);
  if (declResult !== null) return declResult;
  const bodyResult = checkStatements(program.body, mainNames);
  if (bodyResult !== null) return bodyResult;

  for (const sub of program.subprograms) {
    // Κάθε παράμετρος πρέπει να έχει ΡΗΤΗ δήλωση στο ΜΕΤΑΒΛΗΤΕΣ του
    // υποπρογράμματος (σύμβαση βιβλίου) — δεν θεωρείται σιωπηλά "γνωστή"
    // μόνο επειδή εμφανίζεται στη λίστα παραμέτρων.
    const names = collectDeclaredNames(sub.declarations);
    if (sub.kind === 'FunctionDeclaration') names.add(sub.name);
    const subDeclResult = checkDeclarationExpressions(sub.declarations, names);
    if (subDeclResult !== null) return subDeclResult;
    const subBodyResult = checkStatements(sub.body, names);
    if (subBodyResult !== null) return subBodyResult;
  }
  return null;
}
